import { Typography } from '@mui/material';
import React, { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UseQueryResult } from 'react-query';

import {
  CrudTable,
  DataGridColumnDefinition,
  StylizedNumber,
} from 'components';
import { TranslationNamespace } from 'i18n';
import { PayinDurationPercentiles, PayinSciFunnel, PayinTimings } from 'types';

type Props = {
  queryResult: UseQueryResult<PayinTimings>;
};

export const TimingsBlock: React.FC<Props> = ({ queryResult }) => {
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.statistics.provider_conversion.timings',
  });

  const durationQueryResult = useMemo(() => {
    const duration = queryResult.data?.paymentDuration;
    return {
      ...queryResult,
      data: duration?.count ? [duration] : [],
    } as UseQueryResult<PayinDurationPercentiles[]>;
  }, [queryResult]);

  const funnelQueryResult = useMemo(() => {
    const funnel = queryResult.data?.sciFunnel;
    return {
      ...queryResult,
      data: funnel?.openedCount ? [funnel] : [],
    } as UseQueryResult<PayinSciFunnel[]>;
  }, [queryResult]);

  const durationColumns = useMemo(
    (): DataGridColumnDefinition<PayinDurationPercentiles>[] => [
      {
        header: t('payment_duration.count'),
        valueGetter: (item) => (
          <StylizedNumber value={item.count} unit={tCommon('units.items')} />
        ),
      },
      {
        header: t('payment_duration.median'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.medianMinutes}
            unit={tCommon('units.minutes')}
          />
        ),
      },
      {
        header: t('payment_duration.p90'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.p90Minutes}
            unit={tCommon('units.minutes')}
          />
        ),
      },
      {
        header: t('payment_duration.p99'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.p99Minutes}
            unit={tCommon('units.minutes')}
          />
        ),
      },
    ],
    [t, tCommon],
  );

  const funnelColumns = useMemo(
    (): DataGridColumnDefinition<PayinSciFunnel>[] => [
      {
        header: t('sci_funnel.opened'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.openedCount}
            unit={tCommon('units.items')}
          />
        ),
      },
      {
        header: t('sci_funnel.loaded'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.loadedCount}
            unit={tCommon('units.items')}
          />
        ),
      },
      {
        header: t('sci_funnel.requisites_seen'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.requisitesSeenCount}
            unit={tCommon('units.items')}
          />
        ),
      },
      {
        header: t('sci_funnel.paid'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.paidCount}
            unit={tCommon('units.items')}
          />
        ),
      },
      {
        header: t('sci_funnel.conversion'),
        valueGetter: (item) => (
          <StylizedNumber value={item.conversionFromOpened} unit="%" />
        ),
      },
    ],
    [t, tCommon],
  );

  return (
    <Fragment>
      <Typography variant="h6" sx={{ mt: 6, mb: 2 }}>
        {t('title')}
      </Typography>
      <Typography variant="subtitle1" sx={{ mt: 4, mb: 2 }}>
        {t('payment_duration.title')}
      </Typography>
      <CrudTable queryResult={durationQueryResult} columns={durationColumns} />
      <Typography variant="subtitle1" sx={{ mt: 4, mb: 2 }}>
        {t('sci_funnel.title')}
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
        {t('sci_funnel.hint')}
      </Typography>
      <CrudTable queryResult={funnelQueryResult} columns={funnelColumns} />
    </Fragment>
  );
};
